// "Why Campus Visits Matter" - student/parent playbook (#1498). Written
// alongside the Visits & Events build. Follows the house shape: the problem
// in the reader's own terms, then "How Stratia solves this", then "What this
// gets you". No implementation detail - a family does not care how the data
// is gathered, only whether the date is right.

const paper = {
    slug: 'why-campus-visits-matter',
    type: 'whitepaper',
    title: 'Why Campus Visits Matter',
    subtitle:
        'A visit answers the questions no ranking can - and families miss them not for lack of interest, but because the dates are scattered across a dozen college websites',
    description:
        "A campus visit is the cheapest way to avoid a four-year mistake, and at some colleges it quietly counts as demonstrated interest. But every college keeps its dates somewhere different, the good slots fill weeks ahead, and by the time a family goes looking it is senior autumn. This paper covers what a visit actually tells you, when to go, what to do while you're there, how to see several colleges in one trip - and how Stratia gets you straight to the right page to book one.",
    readTime: '9 min read',
    category: 'Student Playbook',
    publishedAt: '2026-08-11',

    card: {
        icon: 'CalendarDaysIcon',
        gradient: 'from-cyan-600 to-teal-700',
        accentColor: '#0E7490',
        bgPattern:
            'radial-gradient(circle at 25% 25%, rgba(14,116,144,0.10) 0%, transparent 50%), radial-gradient(circle at 80% 75%, rgba(13,148,136,0.07) 0%, transparent 50%)',
    },

    sections: [
        {
            id: 'what-a-visit-answers',
            title: 'What a visit answers that nothing else can',
            body: `You can learn a college's acceptance rate, its median SAT, what it costs after aid, and what its graduates earn ten years out - all without leaving your kitchen table. Every one of those is worth knowing. None of them tells you whether you'd be happy there.

A visit answers a different class of question. Are students talking to each other in the dining hall, or is everyone alone with a laptop? Is the campus a place, or a set of buildings between parking lots? When you ask a sophomore what they'd change, do they have a ready answer? Can you picture walking to a 9am class here in February?

Families who visit report the same thing over and over: **the list changes.** A college that looked perfect on paper feels wrong in an afternoon, and a polite backup becomes the favourite. That isn't a failure of research - it's the visit doing the one job research can't, and doing it *before* you spend a year writing essays for the wrong school.

There's a second, quieter reason. A meaningful number of colleges track **demonstrated interest** - whether you've engaged with them - and some state plainly that it factors into their decisions. A registered visit is the clearest signal there is. It isn't why you should go. But if you're going anyway, register rather than wandering the campus unrecorded.`,
            visual: {
                component: 'CompareTable',
                props: {
                    leftTitle: 'What research tells you',
                    rightTitle: 'What a visit tells you',
                    rows: [
                        { q: 'The numbers', left: 'admit rate, cost, outcomes', right: 'nothing new' },
                        { q: 'The people', left: 'a viewbook photo', right: 'who actually sits with whom' },
                        { q: 'The place', left: 'a campus map', right: 'whether you want to be there in February' },
                        { q: 'Your list', left: 'confirms it', right: 'often changes it' },
                    ],
                },
            },
        },
        {
            id: 'the-real-problem',
            title: 'The problem isn\'t motivation. It\'s finding the dates.',
            body: `Ask a parent why they haven't booked visits and you'll rarely hear "we don't think it matters". You'll hear that it's a chore - and they're right.

**Every college keeps its dates somewhere different.** One has a booking site on a separate address you'd never guess. One lists its events as plain text halfway down a page. One shows nothing at all until you tell it who you are and what kind of visit you want. One has a busy calendar full of lectures and symposia with nothing on it aimed at applicants. One publishes no dates and simply takes bookings.

So a family with twelve colleges isn't doing one task twelve times. They're learning twelve different websites - and then **re-checking all of them**, because availability moves. Tours fill. Cancellations reopen seats. A date that was full on Sunday has space on Wednesday.

That is the actual barrier. It's why visits slide to the autumn of senior year, when the calendar is already full of deadlines, and why the trip that gets planned is usually one college instead of three.`,
            visual: {
                component: 'CompareTable',
                props: {
                    leftTitle: 'What families assume',
                    rightTitle: 'What\'s actually true',
                    rows: [
                        { q: 'Finding dates', left: 'a quick search', right: 'a different site per college' },
                        { q: 'Availability', left: 'book any time', right: 'popular slots fill weeks ahead' },
                        { q: '"Full"', left: 'means full', right: 'cancellations reopen seats constantly' },
                        { q: 'Best time to plan', left: 'senior autumn', right: 'junior spring, while it\'s calm' },
                    ],
                },
            },
        },
        {
            id: 'timing',
            title: 'When to go, and why earlier is easier',
            body: `The instinct is to visit once you have a shortlist. The trouble is that the shortlist is exactly what a visit is best at correcting - so waiting inverts the order.

**Junior spring is the sweet spot.** School is in session, so you see the campus populated rather than empty; an August visit to a summer-quiet campus tells you very little about living there. You still have time to act on what you learn. And you aren't competing for slots with the senior-autumn rush.

Two practical realities shape everything:

**Bookings open on a rolling window.** Many colleges open roughly **two months ahead**. Look further out and you'll see an empty calendar - which reads as "nothing scheduled" when it actually means "not open yet". Plenty of families conclude a college doesn't run tours on exactly this misreading.

**The good slots fill.** A campus running two tours a day fills them weeks out in peak season. But a full date is rarely the end of it: reservations get cancelled constantly. Checking a full date again a few days later is often all it takes.`,
        },
        {
            id: 'what-to-do',
            title: 'What to actually do while you\'re there',
            body: `The standard tour is a walking advertisement, and that's fine - but it's the floor, not the ceiling. Families who get the most from a day do three things beyond it.

**Go to the department, not just the campus.** A general tour tells you about the dining halls. A session run by the school that would actually teach you - architecture, engineering, nursing - tells you whether you want to spend four years inside it. Many departments run their own information sessions and studio walkthroughs, separate from admissions, and they're usually smaller and franker.

**Ask what the tour guide isn't scripted for.** *What do you wish you'd known before you came? What does everyone complain about? Where do people go on a Tuesday night?* Guides are students. Most will answer honestly if you ask something real.

**Stay after the tour ends.** Eat where students eat. Sit somewhere for twenty minutes and watch. The tour route is chosen to look good; the ordinary parts of campus are where you'd actually live.

If you only do one thing beyond the tour, make it the department session. It's the closest you'll get to a preview of the degree.`,
        },
        {
            id: 'planning-a-trip',
            title: 'Seeing more than one college per trip',
            body: `Visiting is expensive in time before it's expensive in money, which is why single-college trips are hard to justify and clusters are worth building.

The arithmetic is simple: two colleges ninety minutes apart on consecutive days is one trip instead of two. A city with three campuses in it can be a long weekend. The constraints are ordinary - a tour at 10:15 and another at 11:00 two hours away isn't a plan; a Saturday tour at one college and a Monday session at the next means an extra night.

None of that is hard to reason about. **It's only hard to see** - because the dates live in a dozen places that don't know about each other, so nobody ever has them side by side long enough to spot the pairing.`,
        },
        {
            id: 'how-stratia-solves-this',
            title: 'How Stratia solves this',
            body: `One click to the right page for every college on your list:

- **A direct link, not a copy.** Stratia sends you straight to each college's own visit-booking page - verified once, kept as a link, never scraped or re-typed. You always see what the college is actually showing today, because you're looking at their page, not ours.
- **Never stale, because nothing is stored.** A copied calendar goes out of date the moment the college changes something. A link can't - it points at the one place that's always current.
- **If there's no dedicated page yet, you're told, not guessed at.** Some colleges don't publish a separate visit-booking page. When that's the case Stratia says so plainly and sends you to their general admissions page instead - never a dead link, never an invented one.
- **One tap puts it on your roadmap.** Book on the college's own site, then tell Stratia the date. It lands on your family's plan alongside every other deadline, so the whole senior year - visits included - stays in one place even though the booking itself doesn't happen on Stratia.`,
        },
        {
            id: 'what-this-gets-you',
            title: 'What this gets you',
            body: `- **The visit actually happens** - because you're one click from booking, not lost in a dozen browser tabs.
- **The department session, not just the tour** - the part that tells you about the degree.
- **A list you trust** - because you stood on the campuses before you wrote the essays.

Find your fit. Build your plan. Tell your story.`,
        },
    ],
};

export default paper;
